import { startTransition, useLayoutEffect, useRef } from 'react';
import { Object3D, Vector2 } from 'three';
import { shallow } from 'zustand/shallow';

import { useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';

import { BVHSelection } from '../../interactive/BVHSelection.js';

export interface SelectBoxEvent {
  event: PointerEvent;
  selected: Object3D[];
  down: boolean;
  phase: 'start' | 'tick' | 'end';
}

export interface SelectBoxHookOptions {
  /**
   * Whether to enable the select box or not
   */
  enabled?: boolean;
  /**
   * Whether to append the selection or not
   */
  shift?: boolean;
  /**
   * The background color of the select box
   */
  backgroundColor?: React.CSSProperties['backgroundColor'];
  /**
   * The border color of the select box
   */
  borderColor?: React.CSSProperties['borderColor'];
  /**
   * The border width of the select box
   */
  borderWidth?: React.CSSProperties['borderWidth'];
  /**
   * The border style of the select box
   */
  borderStyle?: React.CSSProperties['borderStyle'];
  /**
   * Callback for selection changes
   */
  onSelect: (evt: SelectBoxEvent) => void;
}

export const useSelectBox = <T extends Object3D>(opts?: SelectBoxHookOptions) => {
  const elementRef = useRef<T | null>(null);

  const { camera, get, controls, size, setEvents, gl, raycaster, events } = useThree();
  const parentElement = gl.domElement.parentElement;

  const {
    enabled = true,
    backgroundColor = 'rgba(75, 160, 255, 0.1)',
    borderColor = '#55aaff',
    borderWidth = '1px',
    borderStyle = 'solid',
    onSelect,
  } = { ...opts };

  const border = `${borderWidth} ${borderStyle} ${borderColor}`;

  const handleSelect = useEventCallback((evt: SelectBoxEvent) => {
    onSelect?.(evt);
  });

  useLayoutEffect(() => {
    if (!enabled || !elementRef.current) return;

    const selBox = new BVHSelection(camera, elementRef.current);

    const element = document.createElement('div');
    element.style.pointerEvents = 'none';
    element.style.border = border;
    element.style.backgroundColor = backgroundColor;
    element.style.position = 'fixed';

    const startPoint = new Vector2();
    const pointTopLeft = new Vector2();
    const pointBottomRight = new Vector2();

    const oldRaycasterEnabled = get().events.enabled;
    const oldControlsEnabled = (controls as any)?.enabled;

    let isDown = false;
    let selected: Object3D[] = [];

    function normalize(point: Vector2, target: Vector2) {
      const { x, y } = point;

      target.setComponent(0, ((x - size.left) / size.width) * 2 - 1);
      target.setComponent(1, -((y - size.top) / size.height) * 2 + 1);

      return target;
    }

    function onSelectStart(evt: PointerEvent) {
      handleSelect?.({
        event: evt,
        selected,
        down: true,
        phase: 'start',
      });
      parentElement?.appendChild(element);
      element.style.left = `${evt.clientX}px`;
      element.style.top = `${evt.clientY}px`;
      element.style.width = '0px';
      element.style.height = '0px';
      startPoint.x = evt.clientX;
      startPoint.y = evt.clientY;
    }

    function onSelectMove(evt: PointerEvent) {
      pointBottomRight.x = Math.max(startPoint.x, evt.clientX);
      pointBottomRight.y = Math.max(startPoint.y, evt.clientY);
      pointTopLeft.x = Math.min(startPoint.x, evt.clientX);
      pointTopLeft.y = Math.min(startPoint.y, evt.clientY);
      element.style.left = `${pointTopLeft.x}px`;
      element.style.top = `${pointTopLeft.y}px`;
      element.style.width = `${pointBottomRight.x - pointTopLeft.x}px`;
      element.style.height = `${pointBottomRight.y - pointTopLeft.y}px`;
    }

    function pointerDown(evt: PointerEvent) {
      if (evt.shiftKey) {
        isDown = true;
        selected = [];
        onSelectStart(evt);
      }
    }

    function select(evt: PointerEvent, sp: Vector2, ep: Vector2) {
      let allSelected = selBox.select([
        new Vector2(sp.x, sp.y),
        new Vector2(ep.x, sp.y),
        new Vector2(ep.x, ep.y),
        new Vector2(sp.x, ep.y),
      ]);

      if (!shallow(allSelected, selected)) {
        selected = allSelected;
        handleSelect?.({
          event: evt,
          selected: allSelected,
          down: true,
          phase: 'tick',
        });
      }
    }

    function pointerMove(evt: PointerEvent) {
      if (isDown) {
        if ((controls as any).enabled) (controls as any).enabled = false;
        if (events.enabled) setEvents({ enabled: false });
        const sp = normalize(startPoint, new Vector2());
        const ep = normalize(new Vector2(evt.clientX, evt.clientY), new Vector2());
        if (sp.distanceTo(ep) > 0.03) {
          onSelectMove(evt);
          startTransition(() => {
            select(evt, sp, ep);
          });
        }
      }
    }

    function pointerUp(evt: PointerEvent) {
      if (isDown) {
        if (controls) (controls as any).enabled = oldControlsEnabled;
        setEvents({ enabled: oldRaycasterEnabled });
        isDown = false;
        element.parentElement?.removeChild(element);
        handleSelect?.({
          event: evt,
          selected,
          down: false,
          phase: 'end',
        });
        selected = [];
      }
    }

    const eventsElement = events?.connected ?? gl.domElement ?? document;

    eventsElement.addEventListener('pointerdown', pointerDown, { passive: true });
    eventsElement.addEventListener('pointermove', pointerMove, { passive: true, capture: true });
    eventsElement.addEventListener('pointerup', pointerUp, { passive: true });

    return () => {
      eventsElement.removeEventListener('pointerdown', pointerDown);
      eventsElement.removeEventListener('pointermove', pointerMove);
      eventsElement.removeEventListener('pointerup', pointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size.width,
    size.height,
    raycaster,
    camera,
    controls,
    gl,
    enabled,
    border,
    backgroundColor,
    get,
    size,
    setEvents,
    parentElement,
    handleSelect,
    events?.connected,
  ]);

  return elementRef;
};
